import * as React from 'react'
import styled from 'styled-components'
import { Form, Input, Button, Spin, Tabs, Dropdown, Menu, Tooltip, Table, message } from 'antd'
import { set, get, range, orderBy, isEmpty, cloneDeep } from 'lodash'
import produce from 'immer'
import Select, { Option, getOptionProps } from 'elements/Select'
import Icon from 'elements/Icon'
import Modal from 'elements/Modal'
import { Row, Col } from 'elements/Grid'
import { showError, resourceNotFound, showClientNotifications, ValidationError } from 'helpers/errors'
import { formatUserTime } from 'helpers/dateTime'
import {
  createLabelFactory,
  createFieldFactory,
  createSaveDocumentItems,
  createHandleSubmit,
  isReadOnly,
} from 'helpers/formViews'
import ticketStatus from 'options/tickets/statusOptions'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import Documents from 'containers/Documents/ChildListView'
import Checkbox from 'elements/Checkbox'
import FormButtons from 'elements/FormButtons'

const Container = styled.div`
  .ant-select-search__field__placeholder {
    color: #999 !important;
  }

  .ant-select-selection__placeholder {
    color: #999 !important;
  }
`

class FormView extends React.Component {
  state = { newId: -1 }

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.saveDocumentItems = createSaveDocumentItems(this, 'Ticket')
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.ticketId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getUsers({ onlyUsersInRoleStaff: true }), // getStaffUsers
        this.props.getTicketCategories(),
        itemId
          ? this.props.getDocumentItems({
              request: {
                domainObjectId: itemId,
                domainObjectType: 'Ticket',
                documentType: 'ObjectDocument',
              },
            })
          : Promise.resolve(null),
      ])

      const item = cloneDeep(responses[0].value.data)

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        users: get(responses[2], 'value.data.items', []),
        ticketCategories: get(responses[3], 'value.data.items', []),
        documentItems: get(responses[4], 'value.data.items', []),
        documentItemsOriginal: get(responses[4], 'value.data.items', []),
        documentItemsTotals: get(responses[4], 'value.data.pageTotals', {}),
      })
    } catch (error) {
      showError({ error })
    }
  }

  saveItemSuccess = async (savedItem, saveItemParams) => {
    const response = !saveItemParams.id
      ? await this.props.sendCreateNotification(savedItem.id)
      : await this.props.sendUpdateNotification(savedItem.id)

    showClientNotifications({ response })

    return response
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'closeTicket':
        this.setState(
          produce((draft) => {
            draft.item.status = draft.item.status === 'Closed' ? 'Initial' : 'Closed'
          })
        )
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleAddCommentOk = () => {
    const { comment, internal } = this.state

    this.setState(
      produce((draft) => {
        draft.item.ticketDetails.unshift({
          id: draft.newId,
          internal: Boolean(internal),
          createdByEmail: this.props.user.userName,
          createdDate: new Date().toJSON(),
          message: comment,
        })
        draft.comment = ''
        draft.internal = false
        draft.showAddComment = false
        draft.newId -= 1
      })
    )
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'assignedTo' && isEmpty(value)) {
          draft.item.assignedTo = ''
          draft.item.assignedToEmail = ''
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  saveComments = (ticketId) => {
    const { item } = this.state

    return this.props.createComment(ticketId, {
      ticketDetails: item.ticketDetails
        .filter((each) => each.id < 0)
        .map((each) => ({ ...each, id: 0, ticketId })),
    })
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([this.saveComments(saved.value.data.id), this.saveDocumentItems(saved.value.data.id)])

    return saved
  }

  render() {
    const { item, fieldSettings, comment, internal } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <>
        <Container>
          <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
            {item.id ? (
              <Dropdown
                overlay={
                  <Menu onClick={this.handleActionsMenuClick}>
                    <Menu.Item key="closeTicket">
                      {t(item.status === 'Closed' ? 'reopenTicket' : 'closeTicket')}
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
              >
                <Button className="form-items-actions">
                  {t('actions')}
                  <Icon type="KeyboardArrowDown" />
                </Button>
              </Dropdown>
            ) : null}
            <div className="form-items-container">
              <ValidationError errors={this.state.validationErrors} />
              <Form.Item label={createLabel('title')}>
                {createFieldDecorator('title')(<Input autoFocus />)}
              </Form.Item>
              <Row>
                <Col md={8} xs={24}>
                  <Form.Item label={createLabel('categoryId')}>
                    {createFieldDecorator('categoryId')(
                      <Select disabled={!this.props.userIsInRoleStaff} allowClear={false}>
                        {(this.state.ticketCategories ?? []).map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                  <Form.Item label={createLabel('status')}>
                    {createFieldDecorator('status')(
                      <Select disabled={!this.props.userIsInRoleStaff} allowClear={false}>
                        {Object.entries(ticketStatus).map(([key, value]) => (
                          <Option key={key} value={key}>
                            {t(value)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                  {this.props.userIsInRoleStaff && (
                    <Form.Item label={t('priority')}>
                      {createFieldDecorator('priority')(
                        <Select allowClear={false}>
                          {range(1, 5).map((each) => (
                            <Option key={each} value={each}>
                              {each}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  )}
                </Col>
                <Col md={8} xs={24}>
                  <Form.Item label={t('created')}>
                    <Input value={formatUserTime(item.createdDate, item.createdBy) || t('na')} disabled />
                  </Form.Item>
                  <Form.Item label={t('updated')}>
                    <Input value={formatUserTime(item.updatedDate, item.updatedBy) || t('na')} disabled />
                  </Form.Item>
                </Col>
                <Col md={8} xs={24}>
                  {this.props.userIsInRoleStaff && (
                    <Form.Item label={createLabel('tenant')}>
                      <Input value={item.tenant} autoCorrect="off" autoCapitalize="none" disabled />
                    </Form.Item>
                  )}
                  {this.props.userIsInRoleStaff && (
                    <Form.Item label={createLabel('assignedTo')}>
                      <Select
                        value={item.assignedTo}
                        onChange={(value) => this.setItemValue('assignedTo', value)}
                        placeholder={t('none')}
                        allowClear
                      >
                        <Option value="">{t('none')}</Option>
                        {(this.state.users ?? []).map((each) => (
                          <Option key={each.userName} value={each.userName}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>
              </Row>
              <Tabs
                activeKey={this.state.tabsActiveKey || 'comments'}
                onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
              >
                <Tabs.TabPane key="comments" tab={t('comments')} forceRender>
                  <Row type="flex" justify="space-between" className="whitespace-nowrap mb-12">
                    <Col />
                    <Col className="text-right">
                      <Tooltip title={t('addComment')} placement="topLeft">
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => this.setState({ showAddComment: true, comment: '' })}
                        >
                          <Icon type="Add" bold />
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                  <Table
                    childrenColumnName={[]}
                    rowKey={(record) => record.id}
                    dataSource={orderBy(item?.ticketDetails ?? [], ['createdDate'], ['desc']).map((each) => ({
                      id: each.id,
                      date: formatUserTime(each.createdDate, each.createdByEmail),
                      comment: each.message,
                      internal: each.internal,
                    }))}
                    pagination={false}
                    rowClassName={(record, index) => (record.internal ? 'table-row-warning' : '')}
                  >
                    <Table.Column dataIndex="date" title={t('date')} width="25%" className="align-top" />
                    <Table.Column
                      dataIndex="comment"
                      title={t('comment')}
                      render={(text, record, index) => (
                        <div className="m-0 p-0 whitespace-pre-wrap">{text}</div>
                      )}
                    />
                  </Table>
                </Tabs.TabPane>
                <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                  <Documents
                    domainObjectId={item.id}
                    domainObjectType="Ticket"
                    items={this.state.documentItems ?? []}
                    pageTotals={this.state.documentItemsTotals ?? {}}
                    onChange={(values) => this.setState({ documentItems: values })}
                  />
                </Tabs.TabPane>
              </Tabs>
            </div>
            <div className="form-buttons-container">
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            </div>
          </Form>
        </Container>
        <Modal
          title={t('addComment')}
          visible={this.state.showAddComment}
          okText={t('add')}
          onOk={this.handleAddCommentOk}
          okButtonProps={{ disabled: !comment }}
          onCancel={() => this.setState({ showAddComment: false, comment: '' })}
          width={640}
        >
          <Form.Item label={t('comment')} colon={false}>
            <Input.TextArea
              rows={7}
              value={comment}
              onChange={(e) => this.setState({ comment: e.target.value })}
              autoFocus
            />
          </Form.Item>
          {this.props.userIsInRoleStaff && (
            <Checkbox onChange={(e) => this.setState({ internal: e.target.checked })} checked={internal}>
              {t('internal')}
            </Checkbox>
          )}
        </Modal>
      </>
    )
  }
}

export default Form.create()(FormView)
